/**
 * GraphQL query for the Tyme Group landing page content.
 *
 * Mirrors the `TymeGroupLanding` single type in the CMS
 * (`cms.service.app/src/api/tyme-group-landing`): the `Sections` dynamic zone — every one of its
 * ten section components — plus the `header`, `footer` and `seo` components, fetched in full.
 * Component GraphQL type names follow Strapi's `Component<Category><Name>` convention (e.g.
 * `section.faq-block` → `ComponentSectionFaqBlock`); media fields resolve to `UploadFile`. Shared
 * selections (buttons, media, accordion items, widget-list items) are factored into fragments.
 */
export const TYME_GROUP_LANDING_QUERY: string = `
fragment MediaFields on UploadFile {
  url
  alternativeText
  width
  height
  name
  mime
  # Strapi's generated derivatives (thumbnail/small/medium/large) as a JSON scalar. The cms-image
  # component turns them into a srcset; without them the browser can only fetch the full-size
  # original (three 2000x2000 avatars rendered at 104px were 15 MB of a 16 MB page).
  formats
}

fragment ButtonFields on ComponentButtonButton {
  id
  style
  label
  link
  isExternalLink
  queryParams
  target
  buttonAlignment
  customClass
  buttonIcon {
    ...MediaFields
  }
}

fragment AccordionItemFields on ComponentSharedAccordionItem {
  id
  itemLabel
  itemContent
  itemLabelImage {
    ...MediaFields
  }
}

fragment WidgetListItemFields on ComponentSharedWidgetListItem {
  id
  itemLabel
  itemListMarker {
    ...MediaFields
  }
}

fragment SocialItemFields on ComponentSharedSocialItem {
  id
  label
  link
  target
  customClass
  image {
    ...MediaFields
  }
}

query TymeGroupLanding($status: PublicationStatus, $locale: I18NLocaleCode) {
  tymeGroupLanding(status: $status, locale: $locale) {
    documentId
    locale
    Sections {
      __typename
      ... on ComponentSectionFaqBlock {
        id
        content
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        styles
        blockClassName
        accordionItems {
          ...AccordionItemFields
        }
      }
      ... on ComponentSectionWidgetListBlock {
        id
        imageBlockTitle
        titleContent
        content
        blockId
        blockClassName
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        styles
        widgetListBlockContentPosition
        imageBlockWidthPct
        imageMaxWidthPx
        imageAlignment
        contentBlockWidthPct
        contentBlockMaxWidthPx
        contentAlignment
        image {
          ...MediaFields
        }
        backgroundImage {
          ...MediaFields
        }
        buttonLink {
          ...ButtonFields
        }
        items {
          ...WidgetListItemFields
        }
        search {
          id
          content
          enabledForExpert
          enabledForSeeker
          enabledForGuest
          blockId
          styles
          blockClassName
          mode
        }
      }
      ... on ComponentSectionSuccessStories {
        id
        content
        blockId
        styles
        blockClassName
        storiesList {
          id
          fullName
          shortInfo
          story
          avatarImg {
            ...MediaFields
          }
        }
      }
      ... on ComponentSectionStaticBlock {
        id
        content
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        styles
        blockClassName
        staticBlockContentBlockWidthPct
        staticBlockContentBlockMaxWidthPx
        staticBlockContentAlignment
        signUpButton {
          id
          style
          label
          buttonAlignment
        }
        buttonLink {
          ...ButtonFields
        }
        items {
          ...WidgetListItemFields
        }
        staticBlockBackgroundImage {
          ...MediaFields
        }
      }
      ... on ComponentSectionSliderBlock {
        id
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        styles
        blockClassName
        titleContent
        sliderMode
        sliderItems {
          id
          sliderItemContent
          sliderTabName
          sliderImage {
            ...MediaFields
          }
        }
      }
      ... on ComponentSectionCardsBlock {
        id
        titleContent
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        styles
        blockClassName
        blockId
        position
        backgroundImageDesktop {
          ...MediaFields
        }
        backgroundImageMobile {
          ...MediaFields
        }
        buttonsList {
          ...ButtonFields
        }
        cardItems {
          id
          tagLabel
          content
          image {
            ...MediaFields
          }
          buttonLink {
            ...ButtonFields
          }
        }
      }
      ... on ComponentSectionActionCard {
        id
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        blockClassName
        styles
        actionCardContent
        buttonsList {
          ...ButtonFields
        }
      }
      ... on ComponentSectionStaticBlockWithTags {
        id
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        blockClassName
        styles
        staticBlockContent
        tagsBlockContent
        tagsList {
          id
          tagName
        }
      }
      ... on ComponentSectionStaticBlockWithTagsWizard {
        id
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        blockClassName
        styles
        staticBlockContent
        wizardItems {
          id
          wizardItemTitle
          wizardItemContent
          scrollToAnchor
          scrollToBtnLabel
          tagsList {
            id
            tagName
          }
        }
      }
      ... on ComponentSectionContentGroup {
        id
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        blockClassName
        styles
        contentGroupContent
        itemsList {
          ...AccordionItemFields
        }
      }
      ... on ComponentSectionCardsContentGroup {
        id
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        blockClassName
        styles
        contentGroupContent
        groups {
          id
          title
          content
          backgroundColorHex
          styles
          itemsList {
            ...AccordionItemFields
          }
        }
      }
      ... on ComponentSectionTabsBlock {
        id
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        styles
        blockClassName
        content
        tabsPosition
        tabsItems {
          ...AccordionItemFields
        }
      }
      ... on ComponentSectionHowItWorksBlock {
        id
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        blockId
        styles
        blockClassName
        titleContent
        footerContent
        items {
          id
          content
          image {
            ...MediaFields
          }
        }
        howItWorksBackgroundImage {
          ...MediaFields
        }
      }
      ... on Error {
        code
        message
      }
    }
    header {
      id
      styles
      logo {
        ...MediaFields
      }
      navLinks {
        ...ButtonFields
      }
    }
    footer {
      id
      styles
      linksList {
        id
        content
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        styles
        linksList {
          ...ButtonFields
        }
      }
      contactInfo {
        id
        content
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        styles
        logo {
          ...MediaFields
        }
        linksList {
          ...ButtonFields
        }
      }
      copyrightBlock {
        id
        content
        enabledForExpert
        enabledForSeeker
        enabledForGuest
        styles
        socialLinksEnabled
        socialItems {
          ...SocialItemFields
        }
      }
    }
    seo {
      id
      metaTitle
      metaDescription
      keywords
      metaRobots
      metaViewport
      canonicalURL
      structuredData
      metaImage {
        ...MediaFields
      }
      metaSocial {
        id
        socialNetwork
        title
        description
        image {
          ...MediaFields
        }
      }
    }
  }
}
`;
